import { Box, Chip, DialogContent, DialogTitle, TableCell, TableRow } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import CustomDialog from 'components/custom/CustomDialog';
import { CustomSelect } from 'components/CustomSelect';
import InfiniteScrollVirtuoso, { HeaderInfiniteScrollVirtuoso, InfiniteScrollTableProps, RowInfiniteScrollVirtuoso } from 'components/infinitescrollvirtuoso/InfiniteScrollVirtuoso';
import dayjs from 'dayjs';
import useCatchApiError from 'hooks/useCatchApiError';
import HeaderOuBannerDependendoDaInterpretacaoH1 from 'layout/header-ou-banner-dependendo-da-interpretacao-h1/HeaderOuBannerDependendoDaInterpretacaoH1';
import { useEffect, useState } from 'react';
import { ColumnData, resolve } from 'utils/util';
import { AcaoConferencia, ConferenciaVendaLogDTO } from './conferencia-log.model';
import ConferenciaLogService from './conferencia-log.service';

type Props = {
    open: boolean;
    onCloseHistoricoDialog: () => void;
    uuidPedido: string;
};

const resolveWrapper = (data: any, key: string) => resolve(data, key);

const columns: ColumnData<ConferenciaVendaLogDTO>[] = [
    {
        id: 'sku',
        key: 'skuDescritivo',
        header: 'SKU',
        textAlign: 'left',
        type: 'string',
        width: 40,
        sortable: true,
        filterable: true,
    },
    {
        id: 'caixa',
        key: 'caixa',
        header: 'Sacola',
        textAlign: 'center',
        type: 'string',
        width: 15,
        sortable: true,
        filterable: true,
    },

    {
        id: 'acao',
        key: 'acao',
        header: 'Ação',
        textAlign: 'center',
        type: 'string',
        width: 10,
        sortable: true,
        filterable: true,
        convert: (item, { column }) => {
            const value = resolveWrapper(item, column.key);
            const color: 'success' | 'error' = AcaoConferencia.ADICIONADO === value ? 'success' : 'error';
            return <Chip label={value} color={color} />;
        },

        customFilterJSX: ({ column, filter, setFilter }) => {
            const options = [
                { label: AcaoConferencia.ADICIONADO, value: AcaoConferencia.ADICIONADO },
                { label: AcaoConferencia.REMOVIDO, value: AcaoConferencia.REMOVIDO }
            ];
            return <CustomSelect
                id='acao-conferencia-filtro'
                label={`Filtrar por ${column.header}`}
                value={filter ?? ''}
                options={options}
                optionLabel={'label'}
                optionValue={'value'}
                onSelect={setFilter}
                marginBottom='0'
            />;
        }
    },

    {
        id: 'qtd',
        key: 'quantidade',
        header: 'Quantidade',
        textAlign: 'center',
        type: 'string',
        width: 10,
        sortable: true,
        filterable: true,
    },

    {
        id: 'usuario',
        key: 'usuario',
        header: 'Usuário',
        textAlign: 'center',
        type: 'string',
        width: 15,
        sortable: true,
        filterable: true,
    },
    {
        id: 'dataHora',
        key: 'dataHora',
        header: 'Data/Hora',
        textAlign: 'center',
        type: 'string',
        width: 15,
        sortable: true,
        filterable: true,
        customFilter: ({ filterValue, item }) => {
            const filterDate = new Date(filterValue);
            const [datePart] = item.split(' ');
            const [day, month, year] = datePart.split('/').map(Number);
            const itemDate = new Date(year, month - 1, day);

            return (
                filterDate.getFullYear() === itemDate.getFullYear() &&
                filterDate.getMonth() === itemDate.getMonth() &&
                filterDate.getDate() === itemDate.getDate()
            );
        },
        customFilterJSX: ({ column, filter, setFilter }) => <DatePicker
            disableFuture
            slotProps={{
                textField: {
                    id: `date-picker-filter`,
                    variant: 'standard',
                    onKeyDown: (e: any) => { e.preventDefault(); },
                    error: false
                }
            }}
            label={`Filtrar por ${column.header}`} format='DD/MM/YYYY'
            sx={{
                width: '100%', height: '100%'
            }}
            value={dayjs(filter)}
            onChange={(e: any) => setFilter(e?.$d.toString())}
        />,
    }
];

const initialState = {
    data: [] as ConferenciaVendaLogDTO[]
};

const ConferenciaHistorico = (props: Props) => {

    const conferenciaLogService = ConferenciaLogService();
    const { throwErrorMessage } = useCatchApiError();

    const [localState, setLocalState] = useState(initialState);

    useEffect(() => {
        if (props.open) {
            getLogs();
        }
        //eslint-disable-next-line
    }, [props.open]);

    const getLogs = () => {
        conferenciaLogService.logs(props.uuidPedido)
            .then(({ data }) => {
                setLocalState(prev => ({ ...prev, data }));
            })
            .catch(throwErrorMessage);
    };

    const handleClose = () => {
        props.onCloseHistoricoDialog();
    };

    return (
        <CustomDialog
            open={props.open}
            fullWidth
            onClose={handleClose}
            maxWidth={'lg'}
        >
            <DialogTitle style={{ textAlign: "center" }}>
                <HeaderOuBannerDependendoDaInterpretacaoH1
                    id='conferencia-historico'
                    iconClassName='fa-regular fa-file-lines'
                    label='Histórico da conferência'
                    small
                />
            </DialogTitle>

            <DialogContent
                sx={{
                    overflowX: 'hidden'
                }}
            >
                <InfiniteScrollVirtuoso
                    data={localState.data}
                    initialSort={{
                        orderBy: 'dataHora',
                        orderDirection: 'DESC'
                    }}
                    heightScale={"550px"}
                    fixedHeaderContent={(tableProps) => <HeaderInfiniteScrollVirtuoso {...tableProps} columns={columns} />}
                    renderItem={(tableProps) => <RowInfiniteScrollVirtuoso {...tableProps} columns={columns} sx={{ height: '60px' }} />}
                    fixedFooterContent={(tableProps) => <CustomFooter {...tableProps} />}
                />
            </DialogContent>
        </CustomDialog>
    );
};

const CustomFooter = (param: InfiniteScrollTableProps & { data: ConferenciaVendaLogDTO[]; }) => {
    const { adicionados, removidos } = param.data.reduce((acc, curr) => {
        if (curr.acao === AcaoConferencia.ADICIONADO) {
            acc.adicionados++;
        } else {
            acc.removidos++;
        }
        return acc;
    }, { adicionados: 0, removidos: 0 });

    return <TableRow className='glassmorphism'>
        <TableCell colSpan={999} sx={{ width: param.calcWidth(100) }}>
            <Box width={'100%'} display={'flex'} gap={'15px'} justifyContent={'flex-end'} >
                <span className='footerText'>
                    Adicionados: {adicionados}
                </span>

                <span className='footerText'>
                    Removidos: {removidos}
                </span>
            </Box>
        </TableCell>
    </TableRow>;
};

export default ConferenciaHistorico;
